import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { getConsent, setConsent, hasAnalyticsConsent, loadGoogleAnalytics } from "@/utils/consent";

export default function CookieConsent() {
  const { t } = useTranslation();
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const stored = getConsent();
    if (!stored) {
      setVisible(true);
      return;
    }
    // If previously accepted, load GA on revisit
    if (hasAnalyticsConsent()) {
      loadGoogleAnalytics();
    }
  }, []);

  useEffect(() => {
    if (visible) {
      document.body.classList.add("has-cookie-banner");
    } else {
      document.body.classList.remove("has-cookie-banner");
    }
    return () => {
      document.body.classList.remove("has-cookie-banner");
    };
  }, [visible]);

  const handleConsent = (value: "accepted" | "rejected") => {
    setConsent(value);
    if (value === "accepted") {
      loadGoogleAnalytics();
    }
    setVisible(false);
  };

  const handleClose = () => {
    handleConsent("rejected");
  };

  if (!visible) return null;

  return (
    <div
      className="fixed bottom-0 left-0 right-0 z-40 bg-primary-950 border-t border-primary-800/60"
      role="dialog"
      aria-label={t("cookieConsent.text")}
    >
      <div className="w-full px-4 md:px-10 lg:px-16 py-3 relative">
        {/* Close X */}
        <button
          onClick={handleClose}
          aria-label={t("cookieConsent.close")}
          className="absolute top-1 right-2 md:right-6 w-6 h-6 flex items-center justify-center rounded-full text-background-300 hover:text-white hover:bg-white/10 transition-colors cursor-pointer z-10"
        >
          <span className="w-4 h-4 flex items-center justify-center">
            <i className="ri-close-line text-sm"></i>
          </span>
        </button>

        <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3 pr-6">
          {/* Text */}
          <p className="flex-1 text-xs md:text-sm text-white leading-relaxed min-w-0">
            {t("cookieConsent.text")}
          </p>

          {/* Buttons */}
          <div className="flex items-center gap-2 flex-shrink-0">
            <a
              href="/cookies"
              className="px-3 py-1.5 rounded-md text-xs font-medium text-background-200 hover:text-white hover:bg-white/10 transition-colors cursor-pointer whitespace-nowrap"
            >
              {t("cookieConsent.preferences")}
            </a>
            <button
              onClick={() => handleConsent("rejected")}
              className="px-3 py-1.5 rounded-md text-xs font-medium text-background-200 hover:text-white hover:bg-white/10 transition-colors cursor-pointer whitespace-nowrap"
            >
              {t("cookieConsent.reject")}
            </button>
            <button
              onClick={() => handleConsent("accepted")}
              className="px-3.5 py-1.5 rounded-md text-xs font-semibold text-white brand-gradient-btn cursor-pointer whitespace-nowrap"
            >
              {t("cookieConsent.acceptAll")}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}